import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import {
  Box,
  TextBody,
  TextHeading,
  get,
  Link as MuiLink,
} from '@ally/metronome-ui'
import { FocusDebtAccount } from '../../types'
import ProgressCircle from './ProgressCircle'
import { forecastedPayoffDateString, NoUpperCaseTextBody } from './TableColumns'
import { createDebtPlanLinkAriaLabel } from './utils'

const StyledTextHeading = styled(TextHeading)`
  text-transform: uppercase;
  margin-bottom: ${get.space('xs')};
`

const StyledProgressText = styled(TextBody)`
  text-transform: uppercase;
`

const StyledFlexContainer = styled(Box)`
  display: flex;
  justify-content: space-evenly;

  @media screen and (max-width: 500px) {
    justify-content: flex-start;
  }
`

const StyledMuiLink = styled(MuiLink)(
  ({ theme }) => `
  font-size: ${theme.font.body.lg.size};
  font-weight: bold;
`,
)

export const NoAprDebtPlan = (): JSX.Element => {
  return (
    <Box display="flex" flexDirection="column">
      <TextBody tag="span" size="sm">
        Unavailable
      </TextBody>
      <TextBody tag="span" size="sm">
        (APR needed)
      </TextBody>
    </Box>
  )
}

const MobileDebtPlan = ({
  debtPlan,
}: {
  debtPlan: FocusDebtAccount
}): JSX.Element => {
  const {
    progress,
    name,
    partialAccountNumber,
    currentBalance,
    forecastedPayoff,
    source,
    lastUpdated,
  } = debtPlan

  const notPlaid = source !== 'PLAID'

  const progressComplete = progress === 1
  return (
    <Box
      alignItems="center"
      justifyContent="space-between"
      py="sm"
      px="xs"
      width="100%"
    >
      <StyledTextHeading tag="h3" size="xs">
        Manage Debt
      </StyledTextHeading>

      <StyledFlexContainer>
        <Box
          pr="lg"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <ProgressCircle progress={progress} />
          </div>
          <StyledProgressText tag="span" size="sm" weight="bold">
            Progress
          </StyledProgressText>
        </Box>

        <Box display="flex" flexDirection="column">
          <Box>
            <StyledMuiLink
              text={name}
              allytmln="debtPlan-debtPlanAccount"
              aria-label={createDebtPlanLinkAriaLabel(
                name,
                partialAccountNumber,
              )}
            >
              <Link to="/debt-plan" />
            </StyledMuiLink>
            <TextBody tag="span" size="lg">
              {maskAccountNumber(partialAccountNumber, 'bullets')}
            </TextBody>
          </Box>

          <TextBody tag="p" size="md" weight="bold">
            {asCurrency(currentBalance)}
            <TextBody tag="span" size="md">
              {' '}
              balance
            </TextBody>
          </TextBody>

          {notPlaid && (
            <NoUpperCaseTextBody tag="span" size="xs">
              As of {dayjs(lastUpdated).format('MM/DD/YYYY')}
            </NoUpperCaseTextBody>
          )}

          {forecastedPayoff && (
            <TextBody tag="span" size="md" weight="bold">
              {dayjs(forecastedPayoff).format(
                forecastedPayoffDateString(progress),
              )}
              <TextBody tag="span" size="md">
                {' '}
                {progressComplete ? 'Payoff date' : 'Projected payoff'}
              </TextBody>
            </TextBody>
          )}
        </Box>
      </StyledFlexContainer>
    </Box>
  )
}

export default MobileDebtPlan
